import cn from 'classnames';
import React, {FC} from 'react';
import {useDebouncedValue} from '@rocket-ui/react-tools';
import {
	useFetchAutoApplicationSettings,
	type FullExecutorAutoApplicationSetting,
} from '@rocket-ui/lk-api';
import {getAutoReplyCompanyName} from '../../model/autoReplyCompanies';
import styles from './AutoReplyCompaniesList.module.css';

export type AutoReplyCompaniesListProps = {
	onSelect: (setting: FullExecutorAutoApplicationSetting) => void;
};

function SearchIcon(): React.ReactElement {
	return (
		<svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" aria-hidden>
			<path
				d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Zm8.1 2.1-3.9-3.9"
				stroke="currentColor"
				strokeWidth="1.75"
				strokeLinecap="round"
			/>
		</svg>
	);
}

export const AutoReplyCompaniesList: FC<AutoReplyCompaniesListProps> = ({onSelect}) => {
	const [query, setQuery] = React.useState('');
	const debouncedQuery = useDebouncedValue(query.trim(), 300);

	const {data, isLoading, isFetching} = useFetchAutoApplicationSettings(
		debouncedQuery ? {search: debouncedQuery} : undefined,
	);
	const settings = (data?.data ?? []).filter((setting) => setting.id != null);
	const showEmptyState = !isLoading && !debouncedQuery && !isFetching && settings.length === 0;

	if (isLoading && settings.length === 0 && !debouncedQuery) {
		return <p className={styles.stateText}>Загрузка...</p>;
	}

	if (showEmptyState) {
		return (
			<div className={styles.pageEmpty}>
				<h2 className={styles.pageEmptyTitle}>Пока нет автооткликов.</h2>
				<p className={styles.pageEmptyText}>
					Откликнитесь на любое предложение, чтобы настроить новый автоотклик
				</p>
			</div>
		);
	}

	return (
		<div className={styles.root}>
			<div className={styles.searchWrap}>
				<SearchIcon />
				<div className={styles.searchField}>
					<input
						type="text"
						inputMode="search"
						autoComplete="off"
						autoCorrect="off"
						spellCheck={false}
						className={cn(styles.searchInput, query && styles.searchInputWithClear)}
						placeholder="Например, ООО Ромашка"
						aria-label="Поиск компании"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
					/>
					<button
						type="button"
						className={cn(styles.clearButton, !query && styles.clearButtonHidden)}
						aria-label="Очистить поиск"
						aria-hidden={!query}
						tabIndex={query ? 0 : -1}
						disabled={!query}
						onClick={() => setQuery('')}
					>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
							<path
								d="M6 6l12 12M18 6L6 18"
								stroke="currentColor"
								strokeWidth="1.75"
								strokeLinecap="round"
							/>
						</svg>
					</button>
				</div>
			</div>

			{settings.length === 0 ? (
				<p className={styles.emptyState}>Ничего не найдено</p>
			) : (
				<ul className={styles.list} aria-label="Компании для автоотклика">
					{settings.map((setting) => (
						<li key={setting.id} className={styles.item}>
							<button type="button" className={styles.itemButton} onClick={() => onSelect(setting)}>
								{getAutoReplyCompanyName(setting)}
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};
